import { useMemo, useContext, useLayoutEffect, useRef, useState } from "react";
import useSpacing from "../../../core/hooks/useSpacing";
import useBaseStyle from "../../../core/hooks/useBaseStyle";
import useImageStyle from "../../../core/hooks/useImageStyle";
import useContainerStyle from "../../../core/hooks/useContainerStyle";
import { getBackgroundPatternClass } from "../../../core/utils/backgroundPatternUtils";
import { getContainerInsetShadowClass } from "../../../core/utils/containerStyleUtils";
import PreviewContext from "../../../editor/context/PreviewContext";

const MAXWIDTH_CENTERING_DEBUG = "MAXWIDTH_CENTERING_DEBUG";
const __isDev = process.env.NODE_ENV === "development";

/**
 * Shared hook that extracts the common Hero rendering logic used by both
 * HeroManual (column-grid system) and HeroAutomatic (mode-based renderers).
 *
 * Returns all computed values needed to render the hero container:
 * background, spacing, sizing, full-bleed breakout, breakpoint, etc.
 */
export default function useHeroBase(item, providedContainerWidth) {
  const data = item || {};
  const { max_height, background, responsive_height } = data;
  const responsiveHeight = responsive_height !== false;

  // --- Spacing & Styling ---
  const { padding: paddingClasses, margin: marginClasses } = useSpacing(data);
  const verticalMarginClasses = useMemo(() => {
    const tokens = String(marginClasses || "")
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    return tokens
      .filter((t) => t.startsWith("mt-") || t.startsWith("mb-") || t.startsWith("my-"))
      .join(" ");
  }, [marginClasses]);
  const spacingClasses = useMemo(
    () => [paddingClasses, verticalMarginClasses].filter(Boolean).join(" "),
    [paddingClasses, verticalMarginClasses],
  );
  const { styleClasses, inlineStyles } = useBaseStyle(data);
  const containerStyleClasses = useContainerStyle(data);

  // --- Background ---
  const { styleClasses: imageStyleClasses, inlineStyles: imageInlineStyles } =
    useImageStyle(background);

  const showImage = background?.show_image;
  const bgImage =
    showImage && background?.background_image_url
      ? background.background_image_url
      : null;
  const bgPattern = background?.background_pattern;
  const { colorClass = "", colorStyle = {}, jsPatternLayer = null } = bgImage
    ? {}
    : getBackgroundPatternClass(bgPattern);

  // --- Container classes ---
  const hasRoundedBorder = /\brounded(-(?!none)\S+)?\b/.test(containerStyleClasses || "");
  const overflowClass = hasRoundedBorder ? "overflow-hidden" : "overflow-visible";
  const containerClasses = [spacingClasses, styleClasses, containerStyleClasses, "relative", overflowClass]
    .filter(Boolean)
    .join(" ");

  // --- Full-bleed breakout ---
  const heroRootRef = useRef(null);
  const [pageInsets, setPageInsets] = useState(null);
  const lastInsetsRef = useRef(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const el = heroRootRef.current;
    if (!el) return;

    const shellEl = el.closest('[data-page-shell="true"]');
    const contentEl = el.closest('[data-page-content="true"]');
    if (!shellEl || !contentEl) {
      setPageInsets(null);
      return;
    }

    const measure = () => {
      try {
        const shellRect = shellEl.getBoundingClientRect();
        const contentRect = contentEl.getBoundingClientRect();
        if (!shellRect.width || !contentRect.width) return;

        const cs = window.getComputedStyle(contentEl);
        const padLeft = parseFloat(cs.paddingLeft || "0") || 0;
        const padRight = parseFloat(cs.paddingRight || "0") || 0;

        const contentBoxLeft = contentRect.left + padLeft;
        const contentBoxRight = contentRect.right - padRight;

        const left = Math.max(0, contentBoxLeft - shellRect.left);
        const right = Math.max(0, shellRect.right - contentBoxRight);
        const contentWidth = Math.max(0, shellRect.width - left - right);

        if (!Number.isFinite(left) || !Number.isFinite(right) || !Number.isFinite(contentWidth)) return;
        if (contentWidth <= 0) return;

        const next = {
          left: Math.round(left),
          right: Math.round(right),
          contentWidth: Math.round(contentWidth),
        };

        setPageInsets((prev) => {
          if (!prev) return next;
          const same =
            Math.abs((prev.left ?? 0) - next.left) < 2 &&
            Math.abs((prev.right ?? 0) - next.right) < 2 &&
            Math.abs((prev.contentWidth ?? 0) - next.contentWidth) < 2;
          return same ? prev : next;
        });

        if (__isDev) {
          try {
            const prev = lastInsetsRef.current;
            const changed =
              !prev ||
              Math.abs((prev.left ?? 0) - next.left) >= 2 ||
              Math.abs((prev.right ?? 0) - next.right) >= 2 ||
              Math.abs((prev.contentWidth ?? 0) - next.contentWidth) >= 2;
            if (changed) {
              lastInsetsRef.current = next;
              const hr = el.getBoundingClientRect?.();
              console.log(MAXWIDTH_CENTERING_DEBUG, "Hero breakout insets", {
                heroId: data?.id,
                left: next.left,
                right: next.right,
                contentWidth: next.contentWidth,
                heroRect: hr
                  ? {
                      left: Math.round(hr.left),
                      right: Math.round(hr.right),
                      width: Math.round(hr.width),
                    }
                  : null,
              });
            }
          } catch {}
        }
      } catch {
        // Non-fatal: keep last known insets
      }
    };

    measure();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      try { ro.observe(shellEl); } catch {}
      try { ro.observe(contentEl); } catch {}
    }

    let mo = null;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => measure());
      try {
        mo.observe(contentEl, { attributes: true, attributeFilter: ["class", "style"] });
      } catch {}
    }

    return () => {
      try { ro?.disconnect(); } catch {}
      try { mo?.disconnect(); } catch {}
    };
  }, [data?.id]);

  // --- Breakpoint detection ---
  const preview = useContext(PreviewContext);
  const shellWidth = providedContainerWidth
    ? providedContainerWidth
    : (preview && typeof preview.contentWidth === "number")
      ? preview.contentWidth
      : (preview && preview.mode !== "responsive" && preview.viewport?.width
        ? preview.viewport.width
        : (typeof window !== "undefined" ? window.innerWidth : 1024));

  const hasExplicitPreviewWidth = preview && typeof preview.contentWidth === "number" && preview.contentWidth > 0;
  const width = (!hasExplicitPreviewWidth && pageInsets && Number.isFinite(pageInsets.contentWidth) && pageInsets.contentWidth > 0)
    ? pageInsets.contentWidth
    : shellWidth;

  const bp = width >= 1280 ? "extra_large" : width >= 1024 ? "large" : width >= 768 ? "medium" : width >= 640 ? "small" : "extra_small";

  const isPhonePreviewMode = preview?.mode === "phone";
  const forceSingleColumn = isPhonePreviewMode || bp === "extra_small";

  // --- Height ---
  const resolveMaxHeightPx = (mh) => {
    if (!mh || typeof mh !== "object") return 300;
    const v = mh[bp] ?? mh.medium ?? mh.small ?? mh.extra_small ?? 300;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 300;
  };

  const baseHeightPx = resolveMaxHeightPx(max_height);

  const containerStyle = {
    width: "100%",
    position: "relative",
    ...inlineStyles,
    ...(responsiveHeight ? { minHeight: `${baseHeightPx}px` } : { height: `${baseHeightPx}px` }),
  };

  const fullBleedStyle = pageInsets
    ? {
        marginLeft: `-${pageInsets.left || 0}px`,
        marginRight: `-${pageInsets.right || 0}px`,
        width: `calc(100% + ${(pageInsets.left || 0) + (pageInsets.right || 0)}px)`,
      }
    : {};

  const contentInsetStyle = pageInsets
    ? {
        paddingLeft: `${pageInsets.left || 0}px`,
        paddingRight: `${pageInsets.right || 0}px`,
      }
    : null;

  const insetShadowClass = getContainerInsetShadowClass(item?.container_style);

  return {
    data,
    heroRootRef,
    containerClasses,
    containerStyle,
    fullBleedStyle,
    contentInsetStyle,
    responsiveHeight,
    baseHeightPx,
    // Background props (for <Background /> component)
    bgImage,
    imageStyleClasses,
    imageInlineStyles,
    colorClass,
    colorStyle,
    jsPatternLayer,
    insetShadowClass,
    // Breakpoint and sizing
    bp,
    width,
    shellWidth,
    forceSingleColumn,
    preview,
    pageInsets,
  };
}
